/**
 * PanoramaControls - Mouse/touch controls for looking around in panorama view
 * Uses Euler angles for consistency with calibration tool
 */

import * as THREE from 'three';

export class PanoramaControls {
  constructor(camera, domElement) {
    this.camera = camera;
    this.domElement = domElement;
    
    // Rotation state (Euler angles in degrees)
    this.rotY = 0; // horizontal (yaw)
    this.rotX = 0; // vertical (pitch)
    this.targetRotY = 0;
    this.targetRotX = 0;

    // Mouse/touch state
    this.isUserInteracting = false;
    this.onPointerDownMouseX = 0;
    this.onPointerDownMouseY = 0;
    this.onPointerDownRotY = 0;
    this.onPointerDownRotX = 0;
    this.updatePending = false;

    // Settings
    this.rotationSpeed = 0.1;
    this.dampingFactor = 0.3;
    this.minRotX = -85; // vertical limits
    this.maxRotX = 85;
    this.enabled = true;

    // Inertia
    this.velocity = { x: 0, y: 0 };
    this.inertiaDamping = 0.5;
    this.inertiaEnabled = false;
    
    // Callback for immediate panorama updates during camera movement
    this.onCameraMove = null;

    this.setupEventListeners();
  }

  /**
   * Setup event listeners
   */
  setupEventListeners() {
    // Mouse events
    this.domElement.addEventListener('mousedown', this.onPointerDown.bind(this));
    this.domElement.addEventListener('mousemove', this.onPointerMove.bind(this));
    this.domElement.addEventListener('mouseup', this.onPointerUp.bind(this));
    this.domElement.addEventListener('mouseleave', this.onPointerUp.bind(this));

    // Touch events
    this.domElement.addEventListener('touchstart', this.onPointerDown.bind(this));
    this.domElement.addEventListener('touchmove', this.onPointerMove.bind(this));
    this.domElement.addEventListener('touchend', this.onPointerUp.bind(this));

    // Prevent context menu
    this.domElement.addEventListener('contextmenu', (e) => e.preventDefault());

    // Mouse wheel for vertical look
    this.domElement.addEventListener('wheel', this.onMouseWheel.bind(this), { passive: false });
  }

  /**
   * Handle pointer down event
   */
  onPointerDown(event) {
    if (!this.enabled) return;

    this.isUserInteracting = true;
    const clientX = event.clientX || (event.touches && event.touches[0].clientX);
    const clientY = event.clientY || (event.touches && event.touches[0].clientY);

    this.onPointerDownMouseX = clientX;
    this.onPointerDownMouseY = clientY;
    this.onPointerDownRotY = this.rotY;
    this.onPointerDownRotX = this.rotX;

    // Reset velocity
    this.velocity = { x: 0, y: 0 };

    this.domElement.style.cursor = 'grabbing';
    this.domElement.dataset.state = 'grabbing';
  }

  /**
   * Handle pointer move event
   */
  onPointerMove(event) {
    if (!this.enabled || !this.isUserInteracting) return;

    const clientX = event.clientX || (event.touches && event.touches[0].clientX);
    const clientY = event.clientY || (event.touches && event.touches[0].clientY);

    const deltaX = clientX - this.onPointerDownMouseX;
    const deltaY = clientY - this.onPointerDownMouseY;

    if(!deltaX){
      return;
    }

    // Horizontal: no modulo, just linear addition
    this.targetRotY = this.onPointerDownRotY + deltaX * this.rotationSpeed;
    // Vertical with clamping
    this.targetRotX = Math.max(this.minRotX, Math.min(this.maxRotX, this.onPointerDownRotX + deltaY * this.rotationSpeed));

    this.velocity.x = deltaX * 0.01;
    this.velocity.y = deltaY * 0.01;
    
    // Immediately apply rotation during drag for instant response
    this.rotY = this.targetRotY;
    this.rotX = this.targetRotX;
    
    // Update camera rotation immediately
    this.camera.rotation.order = 'YXZ';
    this.camera.rotation.y = THREE.MathUtils.degToRad(this.rotY);
    this.camera.rotation.x = THREE.MathUtils.degToRad(this.rotX);
    this.camera.rotation.z = 0;
    
    // Use requestAnimationFrame to ensure panorama updates on next frame
    // This prevents overwhelming the system during rapid mouse movement
    if (!this.updatePending && this.onCameraMove) {
      this.updatePending = true;
      requestAnimationFrame(() => {
        if (this.onCameraMove) {
          this.onCameraMove();
        }
        this.updatePending = false;
      });
    }
  }

  /**
   * Handle pointer up event
   */
  onPointerUp(event) {
    if (!this.enabled) return;

    this.isUserInteracting = false;
    this.domElement.style.cursor = 'grab';
    this.domElement.dataset.state = 'grab';

    // Snap target to current rotation to prevent drift
    this.targetRotY = this.rotY;
    this.targetRotX = this.rotX;
  }

  /**
   * Handle mouse wheel event
   */
  onMouseWheel(event) {
    if (!this.enabled) return;

    event.preventDefault();
    this.targetRotX += event.deltaY * 0.05;
    this.targetRotX = Math.max(this.minRotX, Math.min(this.maxRotX, this.targetRotX));
  }

  /**
   * Update rotation
   */
  update() {
    if (!this.enabled) return;

    // Apply inertia if enabled
    if (!this.isUserInteracting && this.inertiaEnabled) {
      if (Math.abs(this.velocity.x) > 0.01 || Math.abs(this.velocity.y) > 0.01) {
        this.targetRotY -= this.velocity.x * 10;
        this.targetRotX += this.velocity.y * 10;
        this.targetRotX = Math.max(this.minRotX, Math.min(this.maxRotX, this.targetRotX));

        this.velocity.x *= this.inertiaDamping;
        this.velocity.y *= this.inertiaDamping;
      }
    } else if (!this.isUserInteracting) {
      this.velocity = { x: 0, y: 0 };
    }

    // Smooth interpolation with shortest-path for horizontal rotation
    this.rotY = this.lerpAngle(this.rotY, this.targetRotY, this.dampingFactor);
    this.rotX += (this.targetRotX - this.rotX) * this.dampingFactor;

    // Apply rotation to camera
    this.camera.rotation.order = 'YXZ';
    this.camera.rotation.y = THREE.MathUtils.degToRad(this.rotY);
    this.camera.rotation.x = THREE.MathUtils.degToRad(this.rotX);
    this.camera.rotation.z = 0;
  }

  // Linear interpolation for angles, shortest path
  lerpAngle(a, b, t) {
    let diff = (b - a + 540) % 360 - 180;
    return a + diff * t;
  }

  /**
   * Set rotation
   */
  setRotation(rotY, rotX = 0) {
    this.rotY = rotY;
    this.rotX = rotX;
    this.targetRotY = rotY;
    this.targetRotX = rotX;
  }

  /**
   * Reset rotation to 0
   */
  reset() {
    this.setRotation(0, 0);
  }

  /**
   * Enable/disable controls
   */
  setEnabled(enabled) {
    this.enabled = enabled;
    this.domElement.style.cursor = enabled ? 'grab' : 'default';
  }

  /**
   * Clean up event listeners
   */
  dispose() {
    this.domElement.removeEventListener('mousedown', this.onPointerDown);
    this.domElement.removeEventListener('mousemove', this.onPointerMove);
    this.domElement.removeEventListener('mouseup', this.onPointerUp);
    this.domElement.removeEventListener('mouseleave', this.onPointerUp);
    this.domElement.removeEventListener('touchstart', this.onPointerDown);
    this.domElement.removeEventListener('touchmove', this.onPointerMove);
    this.domElement.removeEventListener('touchend', this.onPointerUp);
    this.domElement.removeEventListener('wheel', this.onMouseWheel);
  }
}
